/**
 * MATERIAL CLEANUP (2026-10-05, Matt: "We don't want old flat sidings and materials … I want all
 * the current models cleaned up … Only the old ones go and any that can be inferred and replaced
 * with the new, they get replaced … I don't want remnants of the old system in place.")
 *
 * A one-time, idempotent pass run on model open (the migrateCaseworkToFreestanding slot):
 *   1. REPLACE — an old material with a clear new equivalent is swapped for it wherever it is
 *      referenced (painted wall finishes and bands, roof planes, floor/ceiling areas, element and
 *      type params, assembly layers, other materials' fields). The new material is added from the
 *      curated set when the model doesn't have it yet.
 *   2. STRIP — an old material with no equivalent is taken off wherever it is applied (the
 *      painted finish goes back to unpainted).
 *   3. REMOVE — once nothing references an old material, it leaves the model's list.
 *   4. NO IMAGES — every material's photo texture is dropped; its solid color stays (Matt: solid
 *      colors, no texture images).
 * Matching is by NAME (firm-subscribed and local copies alike). Running it again changes nothing.
 */
import type { ModelDoc } from './doc'
import { maps, transact, listElements, listTypes, listAssemblies, listMaterials, recordToYMap } from './doc'
import { setElementParams } from './commands/elements'
import { updateType, updateAssembly, materializeTypeNames } from './commands/library'
import { CLADDING_MATERIALS } from './wallCladding'
import { ROOFING_BLEND_MATERIALS, ROOF_TILE_MATERIAL_NAMES } from './roofTiles'
import { getColor, listColors, putColors, type ColorRecord } from './colors'
import { newMaterialId } from './ids'
import type { AssemblyId, ElementId, MaterialId, TypeId } from './types'

/** old name → the new material that takes its place */
export const MATERIAL_REPLACEMENTS: Record<string, string> = {
  'Clapboard Siding': 'Fiber Cement Lap Siding, 6" (Hardie Artisan)',
  'Stone Veneer': 'Stone Veneer, Random Ashlar',
  'Brick - Tan Running Bond': 'Brick Veneer',
  'Black Hardware': 'Hardware - Matte Black',
}
/** old materials with no equivalent: taken off wherever applied */
export const MATERIALS_STRIPPED = ['Shingle Siding']

/** the replacement records, when a model doesn't carry them yet */
const REPLACEMENT_RECORDS: Record<string, Record<string, unknown>> = {
  'Hardware - Matte Black': { name: 'Hardware - Matte Black', color: '#2a2a2a', surface: { color: '#2a2a2a' }, category: 'Metal' },
}

function replaceDeep(v: unknown, map: Map<string, string | null>): { value: unknown; changed: boolean } {
  if (typeof v === 'string') return map.has(v) ? { value: map.get(v) ?? null, changed: true } : { value: v, changed: false }
  if (Array.isArray(v)) {
    let changed = false
    const out = v.map((x) => { const r = replaceDeep(x, map); if (r.changed) changed = true; return r.value })
    return changed ? { value: out, changed } : { value: v, changed: false }
  }
  if (v && typeof v === 'object') {
    let changed = false
    const out: Record<string, unknown> = {}
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      const r = replaceDeep(x, map)
      if (r.changed) changed = true
      out[k] = r.value
    }
    return changed ? { value: out, changed } : { value: v, changed: false }
  }
  return { value: v, changed: false }
}

function referencesAny(v: unknown, ids: Set<string>): boolean {
  if (typeof v === 'string') return ids.has(v)
  if (Array.isArray(v)) return v.some((x) => referencesAny(x, ids))
  if (v && typeof v === 'object') return Object.values(v as Record<string, unknown>).some((x) => referencesAny(x, ids))
  return false
}

/** -> how many records changed (0 = already clean) */
export function migrateMaterialCleanup(model: ModelDoc): number {
  const mats = listMaterials(model)
  const byName = (name: string) => mats.find((m) => m.name === name) ?? null
  const old = mats.filter((m) => m.name in MATERIAL_REPLACEMENTS || MATERIALS_STRIPPED.includes(m.name))
  const textured = mats.filter((m) => (m as unknown as Record<string, unknown>).texture != null)
  if (!old.length && !textured.length) return 0

  let n = 0
  transact(model, () => {
    // the id map: old id → new id (replaced) or null (stripped)
    const map = new Map<string, string | null>()
    for (const m of old) {
      if (MATERIALS_STRIPPED.includes(m.name)) { map.set(m.id, null); continue }
      const want = MATERIAL_REPLACEMENTS[m.name]
      let target = byName(want)?.id ?? null
      if (!target) {
        const curated = CLADDING_MATERIALS.find((c) => c.name === want)
        const rec = curated
          ? { name: curated.name, color: curated.color, surface: { color: curated.color }, category: 'Cladding', cladding: curated.spec }
          : REPLACEMENT_RECORDS[want]
        if (rec) {
          const id = newMaterialId()
          maps(model).materials.set(id, recordToYMap({ ...rec, id }))
          target = id
          n++
        }
      }
      map.set(m.id, target)
    }

    if (map.size) {
      for (const el of listElements(model)) {
        const patch: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(el.params ?? {})) {
          const r = replaceDeep(v, map)
          // a stripped finish param goes back to unpainted ('' for material params, null for data)
          if (r.changed) patch[k] = r.value === null ? (typeof v === 'string' && k !== 'finishExterior' && k !== 'finishInterior' ? '' : null) : r.value
        }
        if (Object.keys(patch).length) { setElementParams(model, el.id as ElementId, patch as never); n++ }
      }
      for (const t of listTypes(model)) {
        const patch: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(t.params ?? {})) {
          const r = replaceDeep(v, map)
          if (r.changed) patch[k] = r.value === null ? '' : r.value
        }
        if (Object.keys(patch).length) { updateType(model, t.id as TypeId, { params: patch as never }); n++ }
      }
      for (const a of listAssemblies(model)) {
        // a stripped material in an assembly layer stays (removing a layer would move walls);
        // only replacements apply here
        const layers = a.layers.map((l) => {
          const to = map.get(l.materialId as string)
          return to ? { ...l, materialId: to as MaterialId } : l
        })
        if (layers.some((l, i) => l !== a.layers[i])) { updateAssembly(model, a.id as AssemblyId, { layers }); n++ }
      }
      for (const m of listMaterials(model)) {
        if (map.has(m.id)) continue
        const rec = m as unknown as Record<string, unknown>
        for (const [k, v] of Object.entries(rec)) {
          if (k === 'id') continue
          const r = replaceDeep(v, map)
          if (r.changed) { maps(model).materials.get(m.id)?.set(k, r.value); n++ }
        }
      }
      // REMOVE the old ones nothing references any more
      const stillUsed = new Set<string>()
      const ids = new Set(old.map((m) => m.id as string))
      const scan = (v: unknown) => { if (referencesAny(v, ids)) for (const id of ids) if (referencesAny(v, new Set([id]))) stillUsed.add(id) }
      for (const el of listElements(model)) scan(el.params)
      for (const t of listTypes(model)) scan(t.params)
      for (const a of listAssemblies(model)) scan(a.layers)
      for (const id of ids) {
        if (stillUsed.has(id)) continue
        maps(model).materials.delete(id)
        n++
      }
    }

    // NO IMAGES: drop every photo texture, keep the solid color
    for (const m of textured) {
      const ym = maps(model).materials.get(m.id)
      if (!ym || !ym.has('texture')) continue
      ym.delete('texture')
      n++
    }
  })
  if (n) materializeTypeNames(model)
  return n
}

// ---------- slice 2 (2026-10-06): blends and color variants ----------
// Matt: finishes and colors are separate lists, with no remnants of the old system. A material
// that was really "a finish in a color" becomes the finish + a color from the firm list (the
// surface keeps its look); the roof blend shades go — roofing varies in shades of its own color.

/** every blend-shade material the roofing used to be laid in */
export const ROOF_BLEND_NAMES: string[] = [
  ...ROOFING_BLEND_MATERIALS.map((b) => b.name),
  ...ROOF_TILE_MATERIAL_NAMES.blends,
]

/** old color-variant material → the finish it really is + its color (fixed firm ids) */
export const COLOR_VARIANTS: { old: string; base: string; baseId: string; baseColor: string; color: { id: string; name: string } }[] = [
  { old: 'Cabinet Paint - White (Satin)', base: 'Cabinet Paint (Satin)', baseId: 'finCabPaint1', baseColor: '#efede6', color: { id: 'colCabWhite1', name: 'Cabinet White' } },
  { old: 'Cabinet Paint - Sage (Satin)', base: 'Cabinet Paint (Satin)', baseId: 'finCabPaint1', baseColor: '#efede6', color: { id: 'colCabSage01', name: 'Cabinet Sage' } },
  { old: 'Interior Paint - Eggshell White', base: 'Interior Paint (Eggshell)', baseId: 'finIntPaint1', baseColor: '#eeece6', color: { id: 'colEggWhite1', name: 'Eggshell White' } },
  { old: 'White Painted Cedar', base: 'Cedar Fence Wood', baseId: 'fenceCedar01', baseColor: '#9c6b45', color: { id: 'colCedarWht1', name: 'Painted Cedar White' } },
]

/** which color param a material param feeds, by category (null = the surface takes no color) */
function colorKeyFor(category: string, key: string): string | null {
  if (category === 'casework') return 'paintColor'
  if (category === 'fence') return key === 'infillMaterial' ? 'infillColor' : key === 'surfaceMaterial' ? 'frameColor' : null
  if (category === 'window' || category === 'door') return key === 'shutterMaterial' ? 'shutterColor' : null
  if (category === 'column' || category === 'beam') return key === 'material' ? 'paintColor' : null
  return null
}

/** -> how many records changed (0 = already clean) */
export function migrateColorVariants(model: ModelDoc): number {
  const mats = listMaterials(model)
  const blends = mats.filter((m) => ROOF_BLEND_NAMES.includes(m.name))
  const variants = mats.filter((m) => COLOR_VARIANTS.some((v) => v.old === m.name))
  const blendRefs = mats.filter((m) => {
    const r = (m as unknown as { roofing?: Record<string, unknown> }).roofing
    return !!r && ('blendIds' in r || 'blendNames' in r)
  })
  const tileBlendTypes = listTypes(model).filter((t) => ['tileBlend1', 'tileBlend2', 'tileBlend3'].some((k) => t.params?.[k]))
  if (!blends.length && !variants.length && !blendRefs.length && !tileBlendTypes.length) return 0
  let n = 0
  transact(model, () => {
    // ---- the blends: off every roofing spec and roof type, then gone
    for (const m of blendRefs) {
      const r = { ...((m as unknown as { roofing: Record<string, unknown> }).roofing) }
      delete r.blendIds
      delete r.blendNames
      maps(model).materials.get(m.id)?.set('roofing', r)
      n++
    }
    for (const t of tileBlendTypes) {
      updateType(model, t.id as TypeId, { params: { tileBlend1: '', tileBlend2: '', tileBlend3: '' } as never })
      n++
    }

    // ---- the color variants: finish + color
    const colorsMade: ColorRecord[] = []
    for (const m of variants) {
      const v = COLOR_VARIANTS.find((x) => x.old === m.name)!
      let base = listMaterials(model).find((x) => x.name === v.base)
      if (!base) {
        const rec = m as unknown as Record<string, unknown>
        maps(model).materials.set(v.baseId, recordToYMap({
          id: v.baseId, firmId: v.baseId, name: v.base, color: v.baseColor, surface: { color: v.baseColor },
          ...(rec.category ? { category: rec.category } : {}),
          ...(rec.render ? { render: rec.render } : {}),
          ...(rec.cut ? { cut: rec.cut } : {}),
        }))
        base = listMaterials(model).find((x) => x.id === v.baseId)!
        n++
      }
      const hex = String(((m as unknown as { surface?: { color?: string } }).surface?.color) || m.color || '#ffffff').toLowerCase()
      let colorId = getColor(model, v.color.id) ? v.color.id : listColors(model).find((c) => c.name === v.color.name)?.id
      if (!colorId) {
        colorId = v.color.id
        if (!colorsMade.some((c) => c.id === colorId)) colorsMade.push({ id: v.color.id, name: v.color.name, kind: 'paint', hex, firmPending: true })
      }
      const oldId = m.id as string
      const baseId = base.id as string
      // element params: the finish swaps, and the element takes the color where it can
      for (const el of listElements(model)) {
        const patch: Record<string, unknown> = {}
        for (const [k, val] of Object.entries(el.params ?? {})) {
          if (val === oldId) {
            patch[k] = baseId
            const ck = colorKeyFor(el.category, k)
            if (ck && !el.params[ck]) patch[ck] = colorId
          } else if (val && typeof val === 'object') {
            const r = replaceDeep(val, new Map([[oldId, baseId]]))
            if (r.changed) patch[k] = r.value
          }
        }
        if (Object.keys(patch).length) { setElementParams(model, el.id as ElementId, patch as never); n++ }
      }
      // type params: the type's finish swaps; its elements take the color
      for (const t of listTypes(model)) {
        const keys = Object.entries(t.params ?? {}).filter(([, val]) => val === oldId).map(([k]) => k)
        if (!keys.length) continue
        updateType(model, t.id as TypeId, { params: Object.fromEntries(keys.map((k) => [k, baseId])) as never })
        n++
        for (const el of listElements(model)) {
          if (el.typeId !== t.id) continue
          const patch: Record<string, unknown> = {}
          for (const k of keys) {
            const ck = colorKeyFor(el.category, k)
            if (ck && !el.params[ck] && !patch[ck]) patch[ck] = colorId
          }
          if (Object.keys(patch).length) { setElementParams(model, el.id as ElementId, patch as never); n++ }
        }
      }
      for (const a of listAssemblies(model)) {
        if (!a.layers.some((l) => l.materialId === oldId)) continue
        updateAssembly(model, a.id as AssemblyId, { layers: a.layers.map((l) => (l.materialId === oldId ? { ...l, materialId: baseId as MaterialId } : l)) })
        n++
      }
    }
    if (colorsMade.length) putColors(model, colorsMade)

    // ---- remove the old records nothing references any more
    const goners = [...blends, ...variants].map((m) => m.id as string)
    for (const id of goners) {
      const one = new Set([id])
      const used = listElements(model).some((e) => referencesAny(e.params, one))
        || listTypes(model).some((t) => referencesAny(t.params, one))
        || listAssemblies(model).some((a) => referencesAny(a.layers, one))
        || listMaterials(model).some((m) => m.id !== id && referencesAny(m, one))
      if (used) continue
      maps(model).materials.delete(id)
      n++
    }
  })
  if (n) materializeTypeNames(model)
  return n
}

// ---------- slice 3 (2026-10-07): finish consolidation ----------
// Matt: "Now that we have bare metals as paintable finishes, let's streamline our standing seam
// metal roofing and make it default to zinc … Audit the roofing, wall finishes, flooring, and
// ceilings to remove duplicative materials and just set default colors for those that can be
// changed with the brush." A finish that was really "one finish in one metal / color" becomes
// the one finish + that metal or color on the surface, so every surface keeps its look.

/** the finishes that stay — their firm-standard record (written into a model that lacks it, and
 *  over an older copy so the default look is the new one) */
export const CONSOLIDATED_FINISHES: Record<string, Record<string, unknown>> = {
  ssMetal: {
    id: 'roofmatSSM01', firmId: 'roofmatSSM01', name: 'Standing Seam Metal Roofing', category: 'Roofing',
    color: '#8a8f93', surface: { color: '#8a8f93' }, cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#dfe1e2' },
    roofing: { profile: 'standingSeam', thicknessFt: 0.0104, seamSpacingFt: 16 / 12, seamHeightFt: 1.5 / 12 },
    render: { metalness: 1, roughness: 0.55 }, substrate: 'sheetMetal',
  },
  metalPanel: {
    id: 'mpZinc01', firmId: 'mpZinc01', name: 'Metal Panel Siding', category: 'Cladding',
    color: '#8a8f93', surface: { color: '#8a8f93' }, cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#e4e2de' },
    cladding: { profile: 'vertical', thicknessFt: 0.0417, lineSpacingFt: 16 / 12 },
    render: { metalness: 1, roughness: 0.55 }, substrate: 'sheetMetal',
  },
  woodFloor: {
    id: 'MGE3S_24Dgr8', firmId: 'MGE3S_24Dgr8', name: 'Wood Flooring', category: 'Finishes',
    color: '#ad7f4e', surface: { color: '#ad7f4e' }, floorFinish: { thicknessFt: 0.0625 }, substrate: 'wood',
  },
}
/** the names the kept finish used to go by */
const KEEP_ALIASES: Record<string, string[]> = {
  ssMetal: ['Standing Seam Metal Roofing'],
  metalPanel: ['Metal Panel Siding — Zinc', 'Metal Panel Siding'],
  woodFloor: ['Hardwood Flooring', 'Wood Flooring'],
}
/** old finish name → the kept finish (a CONSOLIDATED_FINISHES key, or '@<name>' for another
 *  finish by name) + the color (a colors.ts ref) its surfaces take */
export const FINISH_CONSOLIDATION: { old: string; keep: string; color: string | null }[] = [
  { old: 'Standing Seam Roofing — Zinc', keep: 'ssMetal', color: null },
  { old: 'Standing Seam Roofing — Copper', keep: 'ssMetal', color: 'bare:copper' },
  { old: 'Metal Panel Siding — Copper', keep: 'metalPanel', color: 'bare:copper' },
  { old: 'Metal Panel Siding — Blackened Steel', keep: 'metalPanel', color: 'bare:blackened' },
  { old: 'Metal Panel Siding — Hot Rolled Steel', keep: 'metalPanel', color: 'bare:hotRolled' },
  { old: 'Metal Panel Siding — Weathering Steel', keep: 'metalPanel', color: 'bare:weathering' },
  { old: 'Wood Flooring - Light Oak', keep: 'woodFloor', color: null },
  // the old flat-sheet shingle: the 3D laminated shingle is the same product, built
  { old: 'Comp. Shingle Roofing', keep: '@Asphalt Shingle — GAF Timberline HDZ', color: null },
]
/** a finish field → the color field beside it (top-level params, then nested entries) */
const COLOR_BESIDE: Record<string, string> = {
  finishExterior: 'colorExterior', finishInterior: 'colorInterior',
  faceBaseExterior: 'faceBaseColorExterior', faceBaseInterior: 'faceBaseColorInterior',
  finish: 'color', above: 'aboveColor', materialId: 'color',
}

/** swap `from` → `to` through a value; where a swapped field has a color beside it, that color
 *  (when not already set) becomes `color` */
function swapWithColor(v: unknown, from: string, to: string, color: string | null): { value: unknown; changed: boolean } {
  if (Array.isArray(v)) {
    let changed = false
    const out = v.map((x) => { const r = swapWithColor(x, from, to, color); if (r.changed) changed = true; return r.value })
    return changed ? { value: out, changed } : { value: v, changed: false }
  }
  if (v && typeof v === 'object') {
    let changed = false
    const out: Record<string, unknown> = { ...(v as Record<string, unknown>) }
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (x === from) {
        out[k] = to
        changed = true
        const ck = COLOR_BESIDE[k]
        if (ck && color && !out[ck]) out[ck] = color
      } else if (x && typeof x === 'object') {
        const r = swapWithColor(x, from, to, color)
        if (r.changed) { out[k] = r.value; changed = true }
      }
    }
    return changed ? { value: out, changed } : { value: v, changed: false }
  }
  return { value: v, changed: false }
}

const keptRecordOf = (model: ModelDoc, key: string) => {
  const rec = CONSOLIDATED_FINISHES[key]
  return listMaterials(model).find((x) => x.firmId === rec.firmId || x.id === rec.id || KEEP_ALIASES[key].includes(x.name)) ?? null
}
const isStale = (cur: Record<string, unknown>, rec: Record<string, unknown>) =>
  cur.name !== rec.name || cur.substrate !== rec.substrate || cur.color !== rec.color

/** -> how many records changed (0 = already consolidated) */
export function migrateFinishConsolidation(model: ModelDoc): number {
  const mats = listMaterials(model)
  const olds = mats.filter((m) => FINISH_CONSOLIDATION.some((c) => c.old === m.name))
  const stale = Object.keys(CONSOLIDATED_FINISHES).filter((key) => {
    const m = keptRecordOf(model, key)
    return !!m && isStale(m as unknown as Record<string, unknown>, CONSOLIDATED_FINISHES[key])
  })
  if (!olds.length && !stale.length) return 0
  let n = 0
  transact(model, () => {
    const keptId = (key: string): string | null => {
      if (key.startsWith('@')) return listMaterials(model).find((x) => x.name === key.slice(1))?.id ?? null
      const rec = CONSOLIDATED_FINISHES[key]
      const have = keptRecordOf(model, key)
      if (have) {
        const ym = maps(model).materials.get(have.id)
        if (ym && isStale(have as unknown as Record<string, unknown>, rec)) {
          for (const [k, v] of Object.entries(rec)) if (k !== 'id') ym.set(k, v)
          n++
        }
        return have.id
      }
      maps(model).materials.set(rec.id as string, recordToYMap(rec))
      n++
      return rec.id as string
    }
    for (const key of stale) keptId(key)
    for (const m of olds) {
      const c = FINISH_CONSOLIDATION.find((x) => x.old === m.name)!
      const to = keptId(c.keep)
      if (!to || to === m.id) continue
      const from = m.id as string
      for (const el of listElements(model)) {
        const r = swapWithColor(el.params ?? {}, from, to, c.color)
        if (r.changed) { setElementParams(model, el.id as ElementId, r.value as never); n++ }
      }
      for (const t of listTypes(model)) {
        const r = replaceDeep(t.params ?? {}, new Map([[from, to]]))
        if (r.changed) { updateType(model, t.id as TypeId, { params: r.value as never }); n++ }
      }
      for (const a of listAssemblies(model)) {
        if (!a.layers.some((l) => l.materialId === from)) continue
        updateAssembly(model, a.id as AssemblyId, { layers: a.layers.map((l) => (l.materialId === from ? { ...l, materialId: to as MaterialId } : l)) })
        n++
      }
      for (const o of listMaterials(model)) {
        if (o.id === from) continue
        for (const [k, v] of Object.entries(o as unknown as Record<string, unknown>)) {
          if (k === 'id') continue
          const r = replaceDeep(v, new Map([[from, to]]))
          if (r.changed) { maps(model).materials.get(o.id)?.set(k, r.value); n++ }
        }
      }
      const std = maps(model).settings.get('colorStandards') as Record<string, unknown> | undefined
      if (std && from in std) {
        const next = { ...std }
        if (!(to in next)) next[to] = next[from]
        delete next[from]
        maps(model).settings.set('colorStandards', next)
        n++
      }
      const one = new Set([from])
      const used = listElements(model).some((e) => referencesAny(e.params, one))
        || listTypes(model).some((t) => referencesAny(t.params, one))
        || listAssemblies(model).some((a) => referencesAny(a.layers, one))
        || listMaterials(model).some((x) => x.id !== from && referencesAny(x, one))
      if (!used) { maps(model).materials.delete(from); n++ }
    }
  })
  if (n) materializeTypeNames(model)
  return n
}
